import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { PageActionsRow } from '../../../../shared/headers/PageActionsRow';
import { RunStatusBadge } from '../common/RunStatusBadge';
import { RunProgress } from '../common/RunProgress';
import { RuleSetChanges } from '../common/RuleSetChanges';
import { ConnectionHint } from '../common/ConnectionHint';
import { ComparisonResult } from './ComparisonResult';
import {
    fetchComparison,
    followComparison,
    stopFollowingComparison
} from '../redux/comparison/actions';
import { ComparisonPhase } from '../redux/comparison/reducers';
import { fetchScenarios } from '../redux/runs/actions';
import { getComparisonState, getScenariosState } from '../redux/selectors';
import {
    redirectTo,
    redirectToPlayerDetails
} from '../../../../routing/redirectService';
import { getBrandsAndJurisdictions } from '../../../../jurisdiction/redux/selectors';
import { setJurisdiction } from '../../../../jurisdiction/redux/actions';
import { BackButton } from '../../../../shared/button/back-button/BackButton';
import {
    findRunSelection,
    MAX_COMPARISON_PLAYER_LIMIT
} from '../simulation.model';
import { SimulationPath } from '../simulation.paths';

/* How many more of the players whose outcome changed "Show more" asks for */
const PLAYERS_PAGE = 100;

/*
  The outcome of a "Test this change": the baseline and the variant are followed until both are over, and then compared. It is made
  from the two ids in the address, so it can be opened again later, or by someone else.
*/
class ComparisonContainer extends Component {
    get baselineRunId() {
        return this.props.match.params.baselineRunId;
    }

    get variantRunId() {
        return this.props.match.params.variantRunId;
    }

    componentDidMount() {
        // The names of the scenarios of the players whose outcome changed
        this.props.fetchScenarios();
        this.props.followComparison({
            baselineRunId: this.baselineRunId,
            variantRunId: this.variantRunId
        });
    }

    componentDidUpdate(prevProps) {
        const { params } = this.props.match;

        if (
            prevProps.match.params.baselineRunId !== params.baselineRunId ||
            prevProps.match.params.variantRunId !== params.variantRunId
        ) {
            this.props.followComparison({
                baselineRunId: params.baselineRunId,
                variantRunId: params.variantRunId
            });
        }
    }

    componentWillUnmount() {
        this.props.stopFollowingComparison();
    }

    tryAgain = () =>
        this.props.followComparison({
            baselineRunId: this.baselineRunId,
            variantRunId: this.variantRunId
        });

    /* The page of a player reads what the selector at the top has selected, so the brand of the run the player is in is selected first. */
    selectionOf = side =>
        findRunSelection(this.props[side], this.props.brandsAndJurisdictions);

    viewPlayer = (side, player) => {
        const selection = this.selectionOf(side);

        if (selection) {
            this.props.setJurisdiction(selection);
            redirectToPlayerDetails(
                player[`${side}PlayerId`],
                player[`${side}Username`]
            );
        }
    };

    showMorePlayers = () =>
        this.props.fetchComparison({
            baselineRunId: this.baselineRunId,
            variantRunId: this.variantRunId,
            playerLimit: Math.min(
                this.props.playerLimit + PLAYERS_PAGE,
                MAX_COMPARISON_PLAYER_LIMIT
            )
        });

    renderRun = (which, run, runId) => (
        <div style={{ flex: 1, marginRight: 25 }}>
            <div className={'sim-label'}>
                <strong>{which}</strong> · run #{runId}
            </div>
            {run ? (
                <div>
                    <div style={{ margin: '8px 0' }}>
                        <RunStatusBadge status={run.status} />
                    </div>
                    <RunProgress progress={run.progress} status={run.status} />
                </div>
            ) : (
                <SpinLoader />
            )}
            <TransparentButton
                onClick={() => redirectTo(SimulationPath.run(runId))}
            >
                Open run
            </TransparentButton>
        </div>
    );

    renderBody() {
        const {
            phase,
            data,
            error,
            baseline,
            variant,
            connection,
            scenariosById
        } = this.props;
        const changes = _.get(variant, 'ruleSet.changes');

        return (
            <div>
                <ConnectionHint
                    connection={connection}
                    onRetry={this.tryAgain}
                />
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Runs'}
                        subtitle={
                            variant && variant.label
                                ? variant.label.replace(/ \([^)]*\)$/, '')
                                : null
                        }
                        content={
                            <div style={{ display: 'flex' }}>
                                {this.renderRun(
                                    'Baseline',
                                    baseline,
                                    this.baselineRunId
                                )}
                                {this.renderRun(
                                    'Variant',
                                    variant,
                                    this.variantRunId
                                )}
                            </div>
                        }
                    />
                </div>
                {changes && (
                    <div className={'sim-section'}>
                        <ContentBox
                            title={'What was changed'}
                            content={<RuleSetChanges changes={changes} />}
                        />
                    </div>
                )}

                {phase === ComparisonPhase.FOLLOWING && (
                    <div className={'sim-banner info'}>
                        <i className={'material-icons'}>hourglass_empty</i>
                        <div>
                            The baseline is played first and then the variant.
                            The comparison is shown when both have completed.
                        </div>
                    </div>
                )}
                {phase === ComparisonPhase.LOADING && !data && <SpinLoader />}
                {phase === ComparisonPhase.FAILED && (
                    <div className={'sim-banner error'}>
                        <i className={'material-icons'}>error</i>
                        <div>
                            {_.get(error, 'message') ||
                                'The runs could not be compared.'}
                        </div>
                    </div>
                )}
                {data && (
                    <ComparisonResult
                        comparison={data}
                        scenariosById={scenariosById}
                        onShowMorePlayers={this.showMorePlayers}
                        loadingMore={phase === ComparisonPhase.LOADING}
                        canViewPlayers={{
                            baseline: !!this.selectionOf('baseline'),
                            variant: !!this.selectionOf('variant')
                        }}
                        onViewPlayer={this.viewPlayer}
                    />
                )}
            </div>
        );
    }

    render() {
        return (
            <div className={'sim-comparison-page'}>
                <PageActionsRow leftContent={<BackButton />} />
                {this.renderBody()}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const comparison = getComparisonState(state);

    return {
        phase: comparison.phase,
        data: comparison.data,
        error: comparison.error,
        baseline: comparison.baseline,
        variant: comparison.variant,
        playerLimit: comparison.playerLimit,
        connection: comparison.connection,
        scenariosById: _.keyBy(getScenariosState(state).data, 'id'),
        brandsAndJurisdictions: getBrandsAndJurisdictions(state)
    };
};

const mapDispatchToProps = dispatch => ({
    followComparison: args => dispatch(followComparison(args)),
    stopFollowingComparison: () => dispatch(stopFollowingComparison()),
    fetchScenarios: () => dispatch(fetchScenarios()),
    fetchComparison: args => dispatch(fetchComparison(args)),
    setJurisdiction: args => dispatch(setJurisdiction(args))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ComparisonContainer);
